import React from 'react';
import { Link } from 'react-router-dom';
import './QAPage.css';

function QAPage() {
  const bannerStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/assets/NeXusNeuroClinical_rib.webp)`,
  };

  return (
    <main className="qa-page">
      <section className="qa-banner" style={bannerStyle}>
        <div className="qa-banner-overlay">
          <Link to="/" className="qa-back">&larr; Home</Link>
          <img
            src="/assets/nexus_neuro_logo.webp"
            alt="NeXus NeuroInformatics, Inc."
            className="qa-banner-logo"
          />
          <h1 className="qa-banner-title">Questions &amp; Answers</h1>
        </div>
      </section>

      <header className="qa-header">
        <p className="qa-deck">
          What researchers, regulatory professionals, and partners ask us most.
          If yours isn&rsquo;t here, reach the office of the CEO via the{' '}
          <Link to="/contact">Contact page</Link>.
        </p>
      </header>

      <section className="qa-list">

        {/* — About the product — */}

        <article className="qa-item">
          <h2 className="qa-q">What is Auditable AI?</h2>
          <div className="qa-a">
            <p>
              Auditable AI is artificial intelligence whose outputs trace back to
              specific training inputs, chemistry-conditioned context, and reasoning
              paths. Where most AI systems produce answers without provenance,
              NeXus&rsquo;s AETHER architecture records every learned fact to an
              auditable file: <em>who taught it, in what context, with what
              neurochemical state</em>. This makes the system reviewable, reversible,
              and defensible to regulatory reviewers.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">What is AETHER technology?</h2>
          <div className="qa-a">
            <p>
              <strong>AETHER</strong> (Adaptive Endocrine Transformer Heads with
              Emotional Regulation) is a transformer architecture inspired by
              biological systems, developed and patented by{' '}
              <a href="https://nexusconcordat.com">Nexus Concordat, Inc.</a> The
              same pharmacokinetic principles that govern how drugs are absorbed,
              distributed, metabolized, and eliminated also govern how the model
              assigns significance to experience over time. NeXus NeuroInformatics
              licenses AETHER exclusively for biomedical research applications.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">How does NeXus relate to Nexus Concordat and Dynamic Hallucination?</h2>
          <div className="qa-a">
            <p>Three sister entities under a common parent:</p>
            <ul>
              <li>
                <strong>Nexus Concordat, Inc.</strong> is the parent corporation.
                It owns and licenses the AETHER intellectual property.
              </li>
              <li>
                <strong>NeXus NeuroInformatics, Inc.</strong> is the FDA-facing
                operating subsidiary. It commercializes AETHER for biomedical
                research, drug development simulation, and regulatory
                decision-support.
              </li>
              <li>
                <strong>Dynamic Hallucination, Inc.</strong> is the consumer-facing
                operating subsidiary. It produces an interactive role-playing
                platform that generates structured behavioral simulation data.
              </li>
            </ul>
            <p>
              NeXus uses training pairs produced by Dynamic Hallucination&rsquo;s
              governed simulation environment to train its biomedical AI.
            </p>
          </div>
        </article>

        {/* — About the demo — */}

        <article className="qa-item">
          <h2 className="qa-q">Is this model currently being used for clinical diagnosis or treatment recommendations?</h2>
          <div className="qa-a">
            <p>
              <strong>No.</strong> It is strictly a non-diagnostic, non-clinical
              Minimum Viable Product. It is a technical proof-of-concept designed
              to demonstrate parallel architecture performance: specifically, how
              conditioning an LLM on biological states alters its reasoning outputs
              compared to a standard textbook transformer using identical seeds and
              parameters.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">If the outputs are not yet clinically accurate, what does this live demo actually prove?</h2>
          <div className="qa-a">
            <p>
              It proves <strong>scalability, generalizability, and platform
              architecture</strong>. By running the cardiac specialist
              (cardiac_gen3) and the psychiatric specialist (psych_gen1) on the
              exact same underlying software framework, we prove that our
              architecture can cleanly digest completely different biological
              corpora and modularly scale into new medical domains without
              rewriting the core software pipeline.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">How does this software scale without requiring massive local hardware footprints for end-users?</h2>
          <div className="qa-a">
            <p>
              Think of our system like a modern streaming service. Instead of
              forcing hospitals or research centers to buy and maintain massive,
              expensive supercomputers on-site, they simply log into our secure,
              cloud-based platform. By using a subscription model, our partners
              always get instant access to a living, constantly improving system
              that learns over time, removing the local-supercomputer requirement
              for participation.
            </p>
          </div>
        </article>

        {/* — About regulatory — */}

        <article className="qa-item">
          <h2 className="qa-q">As you move toward clinical applications, how are you addressing software safety, lifecycle management, and regulatory oversight?</h2>
          <div className="qa-a">
            <p>
              We are actively mapping our core development pipeline to align with
              global medical software standards, specifically <strong>IEC 62304</strong>{' '}
              lifecycle processes and <strong>FDA design controls under 21 CFR
              820.30</strong>. Because our architecture lets us swap in different
              medical domains using the exact same underlying software framework,
              we can build a single, repeatable instruction set for how our
              technology is created, tested, and documented. This gives us a
              substantial structural advantage that shortens the regulatory
              submission timeline for each subsequent product.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">Has NeXus taken any public regulatory positions?</h2>
          <div className="qa-a">
            <p>
              Yes. Nexus Concordat, Inc. submitted a public comment to FDA Docket
              No. <strong>FDA-2026-N-4390</strong> (AI-Enabled Optimization of
              Early-Phase Clinical Trials Pilot Program) on April 29, 2026,
              accompanied by a methods paper titled <em>&ldquo;Mechanistic
              In-Silico Trial Emulation: A Third Evidentiary Substrate for
              Real-World Evidence.&rdquo;</em> The comment is publicly accessible
              at regulations.gov as comment FDA-2026-N-4390-0015.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">How does NeXus&rsquo;s approach to AI drift control differ from standard adaptive AI?</h2>
          <div className="qa-a">
            <p>
              Most adaptive AI systems update via autonomous gradient learning
              from in-the-wild data, which is the well-documented source of drift.
              Our architecture allows post-deployment updates only through a{' '}
              <strong>compiler-enforced verification gate</strong>: candidate
              updates are statically rejected unless they have traversed a
              documented trusted-source verification step. Tier promotion
              (experimental to core) requires multi-signer attestation. Every
              opcode update is version-traceable from creation through every
              supersession, producing an auditable lineage record for each
              callable primitive.
            </p>
            <p>
              We have proposed to the FDA that this{' '}
              <strong>architectural PCCP enforcement</strong>, with cryptographic
              version traceability of every modification, may provide stronger
              drift control than procedural PCCP enforcement alone.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">What is the difference between architectural elimination and post-hoc mitigation of AI safety risks?</h2>
          <div className="qa-a">
            <p>
              Post-hoc mitigation, even when reducing harm by 50% or more, leaves
              a residual risk surface that requires continuous monitoring,
              version-by-version recalibration, and unbounded liability allocation.{' '}
              <strong>Architectural elimination of a class of risk is the higher
              standard.</strong> We have urged the FDA to evaluate AI system
              safety in early-phase trials against the architectural standard
              wherever it is available, while accepting post-hoc mitigation as the
              floor where architectural elimination is not yet achievable.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">How does mechanistic in-silico trial emulation differ from other simulation approaches?</h2>
          <div className="qa-a">
            <p>
              We have proposed to the FDA that &ldquo;simulation studies&rdquo;
              be refined into three distinct evidentiary substrates:
            </p>
            <ol>
              <li>
                <strong>Statistical simulation based on observational data</strong>
                {' '}(for example, the RCT-DUPLICATE methodology applied to
                claims-based real-world evidence).
              </li>
              <li>
                <strong>Hybrid model-data simulation</strong> (for example,
                physiologically-based pharmacokinetic and quantitative systems
                pharmacology methodologies, including the FDA Model-Informed Drug
                Development Pilot Program).
              </li>
              <li>
                <strong>Mechanistic <em>in silico</em> trial emulation</strong>:
                synthetic patient cohorts generated entirely from documented
                biological parameters, with outcomes computed deterministically.
              </li>
            </ol>
            <p>
              NeXus&rsquo;s architecture targets the third substrate.
            </p>
          </div>
        </article>

        {/* — About working with us — */}

        <article className="qa-item">
          <h2 className="qa-q">How does NeXus get its training data?</h2>
          <div className="qa-a">
            <p>
              From its sister company, <strong>Dynamic Hallucination, Inc.</strong>{' '}
              Dynamic Hallucination operates a governed simulation environment in
              which player interactions with persistent characters generate
              structured behavioral data. NeXus uses these training pairs,
              structured to model biological response dynamics, to train its
              biomedical AI.
            </p>
          </div>
        </article>

        <article className="qa-item">
          <h2 className="qa-q">How can my organization partner with NeXus?</h2>
          <div className="qa-a">
            <p>
              NeXus actively seeks research partnerships, regulatory advisory
              relationships, and pre-clinical pilot collaborations. Reach us via
              the <Link to="/contact">Contact page</Link>, or contact the parent
              corporation directly at{' '}
              <a href="https://nexusconcordat.com">nexusconcordat.com</a>.
            </p>
          </div>
        </article>

      </section>
    </main>
  );
}

export default QAPage;
